import React, { useCallback, useMemo } from 'react'
import classNames from 'classnames'
import { Else, If, Then } from 'react-if'
import { CaptionLG, CaptionMD } from '@/atoms/Text'
import { PIF_NAME_IN_CREDITS_MIN_USD } from '@/constants/pif'
import { usePayItForwardOfferData } from '@/contexts/PayItForwardOffersDataContext'
import { Offer } from '@/services/PifService'
import { getPeopleImpacted } from '@/services/PifService/PifService'
import { formatPrice, getPriceForCurrency } from '@/services/PriceFormatter'
import { useLocale } from '@/utils/LocaleUtil'

interface DavidPriceOptionsProps {
  offers: Offer[]
  onClick: (offer: Offer) => void
  selectedOffer: Offer | undefined
  shouldShowPeopleImpacted?: boolean
  shouldUseEqualColumns?: boolean
}

export const DavidPriceOptions: React.FC<DavidPriceOptionsProps> = ({
  offers,
  onClick,
  selectedOffer,
  shouldShowPeopleImpacted = true,
  shouldUseEqualColumns = false,
}) => {
  const { locale } = useLocale()
  const { setNameInCredits } = usePayItForwardOfferData()

  const getReach = useCallback(
    (offer: Offer) => {
      const num = getPeopleImpacted(offer)
      return new Intl.NumberFormat(locale, { notation: 'compact' }).format(num)
    },
    [locale],
  )

  const filteredOffers = useMemo(() => {
    return offers?.filter((offer) => offer.price <= 750000) || []
  }, [offers])

  const handleOptionClick = useCallback(
    (offer: Offer) => {
      const amount = getPriceForCurrency(offer.price, offer.currency)
      if (amount < PIF_NAME_IN_CREDITS_MIN_USD) {
        setNameInCredits('')
      }
      onClick(offer)
    },
    [onClick, setNameInCredits],
  )

  return (
    <>
      <If condition={shouldUseEqualColumns}>
        <Then>
          <div className="grid grid-cols-5 gap-2">
            {filteredOffers.map((offer) => {
              const price = formatPrice(offer.price, { includeDecimals: false, currency: offer.currency })
              const isSelected = offer.id === selectedOffer?.id
              const label = shouldShowPeopleImpacted ? getReach(offer) : price
              return (
                <div
                  key={offer?.id}
                  className="flex-basis-auto shrink-0 grow cursor-pointer text-center"
                  onClick={() => handleOptionClick(offer)}
                >
                  <CaptionMD
                    weight="medium"
                    className={classNames(
                      isSelected
                        ? 'border-[#411E09] bg-[#411E09] text-white'
                        : 'border-[#EDDED1] bg-white text-black hover:bg-[#EDDED1]',
                      'rounded-full border-[1.5px] h-10 py-[.4375rem] px-[1.125rem] select-none flex items-center justify-center',
                    )}
                  >
                    {label}
                  </CaptionMD>
                </div>
              )
            })}
          </div>
        </Then>
        <Else>
          <div className="flex flex-wrap">
            {filteredOffers.map((offer) => {
              const price = formatPrice(offer.price, { includeDecimals: false, currency: offer.currency })
              const isSelected = offer.id === selectedOffer?.id
              const label = shouldShowPeopleImpacted ? getReach(offer) : price
              return (
                <div
                  key={offer?.id}
                  className="flex-basis-auto shrink-0 grow cursor-pointer text-center"
                  onClick={() => handleOptionClick(offer)}
                >
                  <CaptionLG
                    weight="bold"
                    className={classNames(
                      isSelected
                        ? 'border-core-gray-800 bg-core-gray-800 text-white'
                        : 'border-core-gray-200 bg-core-gray-100',
                      'rounded-lg border-2 p-2 select-none',
                    )}
                  >
                    {label}
                  </CaptionLG>
                </div>
              )
            })}
          </div>
        </Else>
      </If>
    </>
  )
}
